import useDocumentHead from '@/hooks/useDocumentHead';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { buildCanonical, buildHreflang, OG_LOCALE_BY_LANG } from '@/utils/seo';
import { SUPPORTED_LANGS, DEFAULT_LANG } from '@/i18n';
import { getAvailableLanguages } from '@/utils/articles';
import { pathFor } from '@/data/routes';

// Minimal author registry for JSON-LD enrichment. We inline this slim
// subset (slug + name + photo + title + socials) instead of importing
// `@/data/authors` so the full 3-lang bios don't bloat the main bundle
// (~10 KB raw / 3.5 KB gzipped saved). The full registry stays in
// data/authors.js for the Author page (lazy-loaded chunk).
const AUTHOR_SEO_DATA = {
  jade: {
    slug: 'jade',
    name: 'Jade',
    photo: '/images/author/jade.jpg',
    title: { fr: 'Co-fondatrice & rédactrice', en: 'Co-founder & editor', es: 'Cofundadora y editora' },
    sameAs: ['https://www.linkedin.com/in/inside-panama'].filter(Boolean),
  },
  natasha: {
    slug: 'natasha',
    name: 'Natasha',
    photo: '/images/author/natasha.jpg',
    title: { fr: 'Co-fondatrice & rédactrice', en: 'Co-founder & editor', es: 'Cofundadora y editora' },
    sameAs: ['https://www.linkedin.com/in/inside-panama'].filter(Boolean),
  },
};

// PNG (not SVG) is required: Facebook, LinkedIn, WhatsApp and iMessage
// do NOT render SVG Open Graph images. PNGs are generated at build
// time from og-{default,en,es}.svg via scripts/build-og.cjs (sharp).
// `og-default.png` = FR (the no-prefix default locale).
const OG_BY_LANG = {
  fr: '/og-default.png',
  en: '/og-en.png',
  es: '/og-es.png',
};

/**
 * Article-aware SEO head. Pass either:
 *   - the bare title/description/image props for non-article pages, OR
 *   - a complete `article` frontmatter object — we'll then derive every meta
 *     tag from it (metaTitle, metaDescription, focusKeyword, schemaType, …).
 *
 * Schema.org JSON-LD is emitted based on `schemaType`:
 *   "Article" | "HowTo" | "ReviewArticle" | "FAQPage". When `faq` is also
 *   provided as `[{question, answer}]`, an additional `FAQPage` block is
 *   appended even on Article pages — this is the recommended pattern.
 *
 * @param {object} props
 * @param {string}        [props.title]
 * @param {string}        [props.description]
 * @param {string}        [props.image]
 * @param {boolean}       [props.noindex=false]
 * @param {object}        [props.article]                  Full frontmatter v2.0.
 * @param {Array<{question:string, answer:string}>} [props.faq]
 *
 * @example
 *   <SEOHead article={frontmatter} faq={frontmatter.faq} />
 */
export default function SEOHead({
  title,
  description,
  image,
  noindex = false,
  article,
  faq,
}) {
  const { i18n } = useTranslation();
  const { pathname } = useLocation();
  const lang = SUPPORTED_LANGS.includes(i18n.language) ? i18n.language : DEFAULT_LANG;

  // Article fields take priority when present.
  const resolvedTitle = article?.metaTitle ?? article?.title ?? title;
  const resolvedDesc = article?.metaDescription ?? article?.excerpt ?? description;
  const defaultOg = OG_BY_LANG[lang] ?? OG_BY_LANG.fr;
  const resolvedImage = article?.ogImage ?? article?.featuredImage ?? image ?? defaultOg;

  // Fallback detection: the Article page falls back to the FR original
  // when an EN/ES translation doesn't exist, but mounts at the EN/ES URL.
  // Without intervention this creates duplicate content (FR body shown at
  // multiple URLs). We patch the canonical to point at the article's
  // native-language URL AND emit noindex,follow so Google consolidates
  // signals on the original.
  const isFallback = !!article && article.lang && article.lang !== lang;
  const canonical = (() => {
    if (isFallback && article.category && article.slug) {
      return `https://insidepanama.com${pathFor('article', article.lang, {
        category: article.category,
        slug: article.slug,
      })}`;
    }
    return buildCanonical(pathname, lang);
  })();
  // For article pages, filter hreflang to only the languages that actually
  // have a published translation — emitting hreflang to a 404 hurts SEO
  // (Google's docs: each alternate must point to a real page). Non-article
  // pages emit all 3 locales as before (they have static fallbacks).
  const allAlternates = buildHreflang(pathname);
  const alternates = (() => {
    if (!article) return allAlternates;
    const master = article.translationOf || article.slug;
    const available = new Set(getAvailableLanguages(master));
    // x-default always emitted (points to FR/home or article master URL)
    return allAlternates.filter(({ lang: hl }) => hl === 'x-default' || available.has(hl));
  })();
  const ogLocale = OG_LOCALE_BY_LANG[lang] ?? OG_LOCALE_BY_LANG[DEFAULT_LANG];
  const ogType = article ? 'article' : 'website';

  // Build the primary JSON-LD payload.
  const ld = article ? buildLD({ article, canonical, lang, image: resolvedImage }) : null;
  const faqLd = (faq?.length || article?.faq?.length) ? buildFaqLD(faq ?? article.faq) : null;

  // Même contenu qu'avant, mais posé par useDocumentHead : <Helmet> n'écrivait
  // rien (vérifié en dev ET en build de production, 0 nœud [data-rh] émis, 0
  // mutation du <head> au changement de route). Les 42 pages qui montent ce
  // composant servaient donc le titre, la description et le canonical de repli
  // d'index.html — canonical sur « / » pour toutes.
  const meta = [
    resolvedDesc && { name: 'description', content: resolvedDesc },
    article?.focusKeyword && { name: 'keywords', content: article.focusKeyword },
    (noindex || article?.status === 'draft' || isFallback) && { name: 'robots', content: 'noindex,follow' },

    { property: 'og:type', content: ogType },
    { property: 'og:url', content: canonical },
    { property: 'og:locale', content: ogLocale },
    ...SUPPORTED_LANGS.filter((l) => l !== lang).map((l) => ({ property: 'og:locale:alternate', content: OG_LOCALE_BY_LANG[l] })),
    resolvedTitle && { property: 'og:title', content: resolvedTitle },
    resolvedDesc && { property: 'og:description', content: resolvedDesc },
    resolvedImage && { property: 'og:image', content: absoluteUrl(resolvedImage) },
    article?.author && { property: 'article:author', content: article.author },
    article?.date && { property: 'article:published_time', content: article.date },
    article?.lastUpdated && { property: 'article:modified_time', content: article.lastUpdated },
    article?.category && { property: 'article:section', content: article.category },

    { name: 'twitter:card', content: 'summary_large_image' },
    resolvedTitle && { name: 'twitter:title', content: resolvedTitle },
    resolvedDesc && { name: 'twitter:description', content: resolvedDesc },
    resolvedImage && { name: 'twitter:image', content: absoluteUrl(resolvedImage) },
  ].filter(Boolean);

  const link = [
    { rel: 'canonical', href: canonical },
    ...alternates.map(({ lang: hl, href }) => ({ rel: 'alternate', hreflang: hl, href })),
  ];

  useDocumentHead({ title: resolvedTitle, lang, meta, link, jsonLd: [ld, faqLd].filter(Boolean) });

  return null;
}

function absoluteUrl(url) {
  if (!url) return url;
  if (/^https?:\/\//.test(url)) return url;
  return `https://insidepanama.com${url.startsWith('/') ? url : `/${url}`}`;
}

function buildLD({ article, canonical, lang, image }) {
  // Enrich author with /auteurs/:slug URL + photo + socials when frontmatter
  // carries an `authorSlug` we can resolve. Stronger E-E-A-T signal: Google
  // links the author Person entity to a real page on the same domain.
  const richAuthor = (() => {
    const slug = article.authorSlug;
    const fallback = article.author
      ? { '@type': 'Person', name: article.author, jobTitle: article.authorTitle }
      : undefined;
    if (!slug || !AUTHOR_SEO_DATA[slug]) return fallback;
    const a = AUTHOR_SEO_DATA[slug];
    const localeTitle = a.title?.[lang] ?? a.title?.fr;
    return {
      '@type': 'Person',
      name: a.name,
      url: `https://insidepanama.com${lang === 'fr' ? '' : `/${lang}`}/auteurs/${a.slug}`,
      ...(a.photo ? { image: absoluteUrl(a.photo) } : {}),
      ...(localeTitle ? { jobTitle: localeTitle } : {}),
      ...(a.sameAs?.length > 0 ? { sameAs: a.sameAs } : {}),
      worksFor: {
        '@type': 'Organization',
        name: 'Inside Panama',
        url: 'https://insidepanama.com',
      },
    };
  })();

  // Multi-part article series → isPartOf > Series. Google interprets this
  // as "Article belongs to Series X, position Y/Z" and may surface the
  // series in rich-results carousels. Only emitted when frontmatter
  // declares `series: { slug, position, title }`.
  const seriesPart = article.series?.slug
    ? {
        isPartOf: {
          '@type': 'Series',
          name: article.series.title || article.series.slug,
          ...(article.series.position ? { position: article.series.position } : {}),
        },
      }
    : {};

  // SpeakableSpecification — tells voice assistants and AI summarizers which
  // CSS selectors carry the article's "speakable" gist (title + standfirst).
  // Google Assistant and Bixby use this; modern LLM crawlers also pick it
  // up as a hint that "these regions are the ones to quote." The selectors
  // below align with the Article.jsx template: <h1> for the headline,
  // [data-speakable="lead"] for the standfirst paragraph.
  const speakable = {
    '@type': 'SpeakableSpecification',
    cssSelector: ['h1', '[data-speakable="lead"]', '[data-speakable]'],
  };

  // about / mentions — give LLMs structured topical hooks. `keywords`
  // duplicates focusKeyword for legacy crawlers; `about` is the modern
  // E-E-A-T equivalent. We split the focusKeyword on commas so authors
  // can pass "friendly nations visa, panama immigration" and get two
  // separate Thing nodes.
  const aboutThings = (article.focusKeyword || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((name) => ({ '@type': 'Thing', name }));

  const base = {
    '@context': 'https://schema.org',
    headline: article.title,
    description: article.metaDescription ?? article.excerpt,
    image: absoluteUrl(image),
    datePublished: article.date,
    dateModified: article.lastUpdated ?? article.date,
    inLanguage: lang,
    mainEntityOfPage: canonical,
    author: richAuthor,
    publisher: {
      '@type': 'NewsMediaOrganization',
      '@id': 'https://insidepanama.com/#organization',
      name: 'Inside Panama',
      logo: { '@type': 'ImageObject', url: 'https://insidepanama.com/logo.png' },
    },
    speakable,
    ...(aboutThings.length > 0 ? { about: aboutThings } : {}),
    ...(article.focusKeyword ? { keywords: article.focusKeyword } : {}),
    ...(article.wordCount ? { wordCount: article.wordCount } : {}),
    ...(article.readTime ? { timeRequired: `PT${article.readTime}M` } : {}),
    ...(article.sources?.length > 0
      ? {
          citation: article.sources.map((s) =>
            typeof s === 'string' ? { '@type': 'CreativeWork', name: s } : s
          ),
        }
      : {}),
    isAccessibleForFree: true,
    ...seriesPart,
  };
  switch (article.schemaType) {
    case 'HowTo':
      return { ...base, '@type': 'HowTo' };
    case 'ReviewArticle':
      return { ...base, '@type': 'ReviewArticle' };
    case 'FAQPage':
      return buildFaqLD(article.faq);
    case 'Article':
    default:
      return { ...base, '@type': 'Article' };
  }
}

function buildFaqLD(faq) {
  if (!faq?.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faq.map((q) => ({
      '@type': 'Question',
      name: q.question,
      acceptedAnswer: { '@type': 'Answer', text: q.answer },
    })),
  };
}
